import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FileText, Send, Plus, Calendar, DollarSign, Package, 
  MapPin, Clock, ArrowLeft, AlertTriangle, Upload, X, 
  CheckCircle, Eye, Download, MessageCircle
} from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { supabase } from '../lib/supabase';
import { useCategories } from '../hooks/useCategories';

const RFQPage = () => {
  const navigate = useNavigate();
  const { user } = useSupabase();
  const { categories } = useCategories();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState<string[]>([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    quantity: '',
    budgetMin: '',
    budgetMax: '',
    deliveryDeadline: '',
    shippingCountry: '',
    requirements: '',
    selectedCategories: [] as string[]
  });

  // Check if user is logged in
  useEffect(() => {
    if (!user) {
      navigate('/signin?redirect=rfq');
    }
  }, [user, navigate]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle category selection
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const options = e.target.options;
    const selectedCategories = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedCategories.push(options[i].value);
      }
    }
    setFormData(prev => ({
      ...prev,
      selectedCategories
    }));
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments = [...attachments];
    const newPreviews = [...attachmentPreviews];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      newAttachments.push(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            newPreviews.push(event.target.result as string);
            setAttachmentPreviews([...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      } else {
        // For non-image files, use a placeholder
        newPreviews.push('file');
      }
    }

    setAttachments(newAttachments);
    if (newPreviews.length > attachmentPreviews.length) {
      setAttachmentPreviews(newPreviews);
    }
  };

  // Remove attachment
  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    const newPreviews = [...attachmentPreviews];
    newAttachments.splice(index, 1);
    newPreviews.splice(index, 1);
    setAttachments(newAttachments);
    setAttachmentPreviews(newPreviews);
  };

  // Upload attachments to storage
  const uploadAttachments = async (): Promise<string[]> => {
    const attachmentUrls: string[] = [];
    
    for (const file of attachments) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `rfq-attachments/${fileName}`;
      
      const { data, error } = await supabase.storage
        .from('attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) throw error;
      
      const { data: { publicUrl } } = supabase.storage
        .from('attachments')
        .getPublicUrl(data.path);
      
      attachmentUrls.push(publicUrl);
    }
    
    return attachmentUrls;
  };

  // Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('You must be logged in to submit an RFQ');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Validate required fields
      if (!formData.title || !formData.description || !formData.quantity || !formData.shippingCountry) {
        setError('Please fill in all required fields');
        setIsSubmitting(false);
        return;
      }
      
      // Validate categories
      if (formData.selectedCategories.length === 0) {
        setError('Please select at least one category');
        setIsSubmitting(false);
        return;
      }
      
      // Upload attachments
      let attachmentUrls: string[] = [];
      if (attachments.length > 0) {
        attachmentUrls = await uploadAttachments();
      }
      
      // Set expiry date (30 days from now)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);
      
      // Create RFQ request
      const { data: rfqData, error: rfqError } = await supabase
        .from('rfq_requests')
        .insert({
          buyer_id: user.id,
          title: formData.title,
          description: formData.description,
          quantity_required: parseInt(formData.quantity),
          budget_min: formData.budgetMin ? parseFloat(formData.budgetMin) : null,
          budget_max: formData.budgetMax ? parseFloat(formData.budgetMax) : null,
          delivery_deadline: formData.deliveryDeadline ? new Date(formData.deliveryDeadline).toISOString() : null,
          shipping_country: formData.shippingCountry,
          attachments: attachmentUrls,
          requirements: formData.requirements,
          expiry_date: expiryDate.toISOString()
        })
        .select()
        .single();
      
      if (rfqError) throw rfqError;
      
      // Link RFQ to categories
      const categoryInserts = formData.selectedCategories.map(categoryId => ({
        request_id: rfqData.id,
        category_id: categoryId
      }));
      
      const { error: categoriesError } = await supabase
        .from('buyer_request_categories')
        .insert(categoryInserts);
      
      if (categoriesError) throw categoriesError;
      
      // Show success message
      setSuccess(true);
      
      // Reset form after 3 seconds and redirect
      setTimeout(() => {
        navigate('/my-rfqs');
      }, 3000);
      
    } catch (err) {
      console.error('Error submitting RFQ:', err);
      setError('Failed to submit RFQ. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Link to="/" className="mr-4 text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Submit Request for Quotation (RFQ)</h1>
              <p className="text-gray-600">Get custom quotes from verified suppliers</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            {success ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="bg-green-100 p-3 rounded-full inline-block mb-4">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">RFQ Submitted Successfully!</h2>
                <p className="text-gray-600 mb-6">
                  Your request for quotation has been submitted. Suppliers will be notified and you'll start receiving quotes soon.
                </p>
                <div className="flex justify-center space-x-4">
                  <Link 
                    to="/my-rfqs" 
                    className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                  >
                    View My RFQs
                  </Link>
                  <Link 
                    to="/" 
                    className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Return to Home
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
                {/* Error Message */}
                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    <span>{error}</span>
                  </div>
                )}
                
                {/* Basic Information */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Basic Information</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                        RFQ Title *
                      </label>
                      <input
                        id="title"
                        name="title"
                        type="text"
                        required
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="e.g., 100 Cotton T-Shirts with Custom Logo"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                        Detailed Description *
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={4}
                        required
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Describe your product requirements in detail..."
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                          Quantity Required *
                        </label>
                        <div className="relative">
                          <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="quantity"
                            name="quantity"
                            type="number"
                            min="1"
                            required
                            value={formData.quantity}
                            onChange={handleChange}
                            className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="e.g., 100"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="deliveryDeadline" className="block text-sm font-medium text-gray-700 mb-2">
                          Delivery Deadline
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="deliveryDeadline"
                            name="deliveryDeadline"
                            type="date"
                            value={formData.deliveryDeadline}
                            onChange={handleChange}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="budgetMin" className="block text-sm font-medium text-gray-700 mb-2">
                          Budget Range (Min)
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="budgetMin"
                            name="budgetMin"
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.budgetMin}
                            onChange={handleChange}
                            className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Min budget per unit"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="budgetMax" className="block text-sm font-medium text-gray-700 mb-2">
                          Budget Range (Max)
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="budgetMax"
                            name="budgetMax"
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.budgetMax}
                            onChange={handleChange}
                            className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Max budget per unit"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="shippingCountry" className="block text-sm font-medium text-gray-700 mb-2">
                        Shipping Destination *
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <select
                          id="shippingCountry"
                          name="shippingCountry"
                          required
                          value={formData.shippingCountry}
                          onChange={handleChange}
                          className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="">Select a country</option>
                          <option value="United States">United States</option>
                          <option value="United Kingdom">United Kingdom</option>
                          <option value="Canada">Canada</option>
                          <option value="Australia">Australia</option>
                          <option value="Germany">Germany</option>
                          <option value="France">France</option>
                          <option value="Japan">Japan</option>
                          <option value="China">China</option>
                          <option value="India">India</option>
                          <option value="Singapore">Singapore</option>
                          <option value="UAE">United Arab Emirates</option>
                          <option value="Saudi Arabia">Saudi Arabia</option>
                          <option value="South Africa">South Africa</option>
                          <option value="Brazil">Brazil</option>
                          <option value="Mexico">Mexico</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Categories */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Product Categories</h2>
                  
                  <div>
                    <label htmlFor="categories" className="block text-sm font-medium text-gray-700 mb-2">
                      Select Categories *
                    </label>
                    <select
                      id="categories"
                      name="categories"
                      multiple
                      required
                      value={formData.selectedCategories}
                      onChange={handleCategoryChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      size={5}
                    >
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Hold Ctrl (or Cmd on Mac) to select multiple categories
                    </p>
                  </div>
                </div>
                
                {/* Additional Requirements */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Additional Requirements</h2>
                  
                  <div>
                    <label htmlFor="requirements" className="block text-sm font-medium text-gray-700 mb-2">
                      Specific Requirements
                    </label>
                    <textarea
                      id="requirements"
                      name="requirements"
                      rows={4}
                      value={formData.requirements}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Any specific requirements like certifications, packaging, shipping terms, etc."
                    />
                  </div>
                  
                  {/* Attachments */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="space-y-4">
                        {attachmentPreviews.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                            {attachmentPreviews.map((preview, index) => (
                              <div key={index} className="relative border border-gray-200 rounded-lg p-2">
                                {preview !== 'file' ? (
                                  <img 
                                    src={preview} 
                                    alt={`Attachment ${index + 1}`} 
                                    className="w-full h-24 object-cover rounded"
                                  />
                                ) : (
                                  <div className="w-full h-24 bg-gray-100 flex items-center justify-center rounded">
                                    <FileText className="h-8 w-8 text-gray-400" />
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute top-1 right-1 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                                <p className="text-xs text-gray-500 mt-1 truncate">
                                  {attachments[index]?.name}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="text-center">
                          <input
                            type="file"
                            id="attachments"
                            multiple
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                          <label
                            htmlFor="attachments"
                            className="cursor-pointer inline-flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            <Upload className="h-4 w-4" />
                            <span>Upload Files</span>
                          </label>
                          <p className="text-xs text-gray-500 mt-2">
                            Upload images, specifications, or any other relevant files (max 5MB each)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        <span>Submit RFQ</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* How It Works */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">How RFQ Works</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-orange-100 p-2 rounded-full text-orange-600 flex-shrink-0">
                    <span className="font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Submit Your Request</h3>
                    <p className="text-sm text-gray-600">Fill out the RFQ form with your product requirements and specifications.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-orange-100 p-2 rounded-full text-orange-600 flex-shrink-0">
                    <span className="font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Receive Quotes</h3>
                    <p className="text-sm text-gray-600">Verified suppliers will review your request and submit their quotes.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-orange-100 p-2 rounded-full text-orange-600 flex-shrink-0">
                    <span className="font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Compare & Select</h3>
                    <p className="text-sm text-gray-600">Compare quotes from different suppliers and choose the best one.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="bg-orange-100 p-2 rounded-full text-orange-600 flex-shrink-0">
                    <span className="font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Finalize Order</h3>
                    <p className="text-sm text-gray-600">Communicate with the supplier and proceed with the order.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tips for Better Quotes */}
            <div className="bg-orange-50 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Tips for Better Quotes</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Be specific about product details and requirements</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Include images or specifications when possible</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Provide realistic budget expectations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Mention any certification or compliance requirements</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Be clear about delivery timeline and location</span>
                </li>
              </ul>
            </div>
            
            {/* Need Help */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h2>
              <p className="text-gray-600 mb-4">
                Our sourcing experts can help you find the right suppliers and get the best quotes.
              </p>
              <button className="w-full bg-gray-800 text-white py-2 rounded-lg font-medium hover:bg-gray-900 transition-colors flex items-center justify-center space-x-2">
                <MessageCircle className="h-4 w-4" />
                <span>Contact Sourcing Expert</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFQPage;